import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, Button, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { capitalize } from 'lodash';
import SpinningSpark from '../shared/components/SpinningSpark';
import styleColors from '../colors.styles';

const styles = {
  modalWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  modalContent: {
    bgcolor: 'white',
    width: '500px',
    padding: 4,
    borderRadius: 4,
    boxShadow: 24,
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: 10,
    right: 10,
  },
  footerBtn: {
    minHeight: '50px',
    borderRadius: 10,
    fontWeight: 600,
  },
};

const TemplateConfirmModal = ({
  templatesData,
  isOpen,
  closeModal,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm(templatesData);
      closeModal();
    } catch (error) {
      console.error('Error confirming:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-description"
      sx={styles.modalWrapper}
    >
      <Box sx={styles.modalContent}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography id="confirm-modal-title" variant="h5">
            This action will end {templatesData?.qty} active templates
          </Typography>
          <IconButton
            onClick={closeModal}
            sx={styles.closeButton}
            aria-label="Close modal"
          >
            <Close />
          </IconButton>
        </Box>

        <Typography id="confirm-modal-description" variant="body1" mb={2}>
          With the {templatesData?.type}:{' '}
          <strong>
            {templatesData?.name ||
              (templatesData?.id
                ? `${capitalize(templatesData?.type)} (ID: ${
                    templatesData?.id
                  })`
                : 'Invalid topic/subtopic')}
          </strong>
        </Typography>

        {templatesData.type === 'topic' && (
          <Box>
            <Typography id="confirm-modal-description" variant="body1" mb={2}>
              Affecting the following subtopics:
            </Typography>
            {templatesData?.subtopics?.length > 0 ? (
              <Box mb={2}>
                {templatesData?.subtopics.map((sub) => (
                  <Typography key={sub.name} variant="body2">
                    - {sub.name || 'Unnamed Subtopic'} ({sub.qty})
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                No subtopics associated.
              </Typography>
            )}
          </Box>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center">
            <SpinningSpark />
          </Box>
        ) : (
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="contained"
              color="secondary"
              sx={{
                ...styles.footerBtn,
                backgroundColor: styleColors.base.inactive,
                color: styleColors.base.black,
              }}
              onClick={closeModal}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                ...styles.footerBtn,
                backgroundColor: styleColors.base.walmart_blue,
              }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

TemplateConfirmModal.propTypes = {
  templatesData: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    qty: PropTypes.number.isRequired,
    subtopics: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        qty: PropTypes.number,
      })
    ),
    type: PropTypes.oneOf(['topic', 'subtopic']).isRequired,
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

export default TemplateConfirmModal;
